import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import PropertyCard from './PropertyCard';
import MobileCarousel from './MobileCarousel';
import MobileSeeMore from './MobileSeeMore';
import { PropertyCardSkeleton } from './Skeleton';

/**
 * PaginatedPropertyGrid
 * Affiche les propriétés par pages de 3, avec :
 *  - flèches Préc / Suiv (desktop) dans le header (rendu par le parent)
 *  - dots de pagination cliquables sous la grille
 *  - autoplay optionnel (rotation auto) avec pause au hover et focus clavier
 *
 * Le composant expose ses contrôles via render-prop `renderControls` pour
 * permettre au parent de placer les flèches dans son propre header.
 */
const PaginatedPropertyGrid = ({
  properties,
  loading,
  pageSize = 3,
  maxItems = 9,
  testIdPrefix = 'paginated',
  emptyLabel = 'Aucune propriété pour le moment.',
  renderControls,
  autoplayMs = 0,
  mobileLimit = 0,
  fetchMore = null,
}) => {
  const items = useMemo(() => (properties || []).slice(0, maxItems), [properties, maxItems]);
  const mobileItems = mobileLimit ? (properties || []).slice(0, mobileLimit) : items;
  const mobilePool = useMemo(() => (mobileLimit ? (properties || []).slice(mobileLimit) : []), [properties, mobileLimit]);
  const mobileIds = useMemo(() => mobileItems.map((p) => p._id || p.id), [mobileItems]);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);

  // clamp page if items shrink
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const visible = items.slice(start, start + pageSize);

  const canPrev = safePage > 0;
  const canNext = safePage < totalPages - 1;

  const goPrev = () => canPrev && setPage(safePage - 1);
  const goNext = () => canNext && setPage(safePage + 1);
  const goTo = (i) => setPage(Math.max(0, Math.min(totalPages - 1, i)));

  // Audit fix 2.1 — swipe gestures on mobile: horizontal delta > 50 px and
  // dt < 500 ms triggers page nav. Wraps around when at either edge so the
  // gesture never feels dead. `touch-action: pan-y` on the grid lets vertical
  // page scroll continue to work unaffected.
  const touchRef = useRef(null);
  const onTouchStart = (e) => {
    if (!e.touches?.length) return;
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = touchRef.current;
    if (!s) return;
    touchRef.current = null;
    const dx = e.changedTouches[0].clientX - s.x;
    const dy = e.changedTouches[0].clientY - s.y;
    const dt = Date.now() - s.t;
    // Ignore vertical scrolls and slow gestures
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) || dt > 500) return;
    if (totalPages <= 1) return;
    if (dx < 0) {
      // swipe left → next (wrap)
      setPage((p) => (Math.min(p, totalPages - 1) + 1) % totalPages);
    } else {
      // swipe right → prev (wrap)
      setPage((p) => (Math.min(p, totalPages - 1) - 1 + totalPages) % totalPages);
    }
  };

  // Autoplay : avance d'une page toutes les `autoplayMs` ms (wrap), pause si hover/focus.
  useEffect(() => {
    if (!autoplayMs || loading || totalPages <= 1 || paused) return undefined;
    // Respect reduced-motion preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return undefined;
    }
    const id = setInterval(() => {
      setPage((p) => {
        const cur = Math.min(p, totalPages - 1);
        return (cur + 1) % totalPages;
      });
    }, autoplayMs);
    return () => clearInterval(id);
  }, [autoplayMs, loading, totalPages, paused]);

  // Expose controls to parent
  const prevButton = (
    <Button
      variant="outline"
      size="icon"
      onClick={goPrev}
      disabled={!canPrev}
      className="rounded-full border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={`${testIdPrefix}-prev`}
      aria-label="Précédent"
    >
      <ChevronLeft className="h-4 w-4" />
    </Button>
  );

  const nextButton = (
    <Button
      variant="outline"
      size="icon"
      onClick={goNext}
      disabled={!canNext}
      className="rounded-full border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={`${testIdPrefix}-next`}
      aria-label="Suivant"
    >
      <ChevronRight className="h-4 w-4" />
    </Button>
  );

  const controls = {
    canPrev,
    canNext,
    goPrev,
    goNext,
    page: safePage,
    totalPages,
    prevButton,
    nextButton,
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      data-testid={`${testIdPrefix}-root`}
    >
      {/* Optional header controls injection point — parent renders them in its own header */}
      {renderControls && renderControls(controls)}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => <PropertyCardSkeleton key={i} />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-stone-500 text-sm">
          {emptyLabel}
        </div>
      ) : (
        <MobileCarousel
          testId={`${testIdPrefix}-mobile`}
          slides={mobileItems.map((p) => <PropertyCard key={p._id || p.id} property={p} />)}
          below={mobileLimit ? (
            <MobileSeeMore pool={mobilePool} excludeIds={mobileIds} fetchMore={fetchMore} testId={testIdPrefix} />
          ) : null}
          desktop={
            <>
              <div
                key={safePage}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-300 touch-pan-y select-none"
                data-testid={`${testIdPrefix}-grid`}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                role="region"
                aria-roledescription="carousel"
                aria-label={`${testIdPrefix} carousel — page ${safePage + 1} sur ${totalPages}`}
              >
                {visible.map((p) => (
                  <PropertyCard key={p._id || p.id} property={p} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8" data-testid={`${testIdPrefix}-dots`}>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => goTo(i)}
                      aria-label={`Page ${i + 1}`}
                      aria-current={i === safePage ? 'page' : undefined}
                      data-testid={`${testIdPrefix}-dot-${i}`}
                      className={[
                        'transition-all rounded-full',
                        i === safePage
                          ? 'w-8 h-2 bg-stone-900'
                          : 'w-2 h-2 bg-stone-300 hover:bg-stone-400',
                      ].join(' ')}
                    />
                  ))}
                </div>
              )}
            </>
          }
        />
      )}
    </div>
  );
};

export default PaginatedPropertyGrid;
